import type { PublicKey } from "@solana/web3.js";
import type { Position, PositionSide, UserAccount } from "./programs/types";
import type { MarketSymbol } from "./constants";
import { OrdersApi, AccountsApi, PositionsApi } from "@/backend/api-client";
import { toBackendOrderRequest } from "@/backend/types";
import type { Position as BackendPosition, Order } from "@nordex/core";

export type OrderType = "market" | "limit";

/**
 * Helper to convert backend Position to frontend Position format
 */
function convertBackendToFrontendPosition(backendPosition: BackendPosition, userId: string): Position {
  const size = parseFloat(backendPosition.size);
  const margin = parseFloat(backendPosition.margin);
  
  return {
    id: `${userId}-${backendPosition.market}`,
    market: backendPosition.market as MarketSymbol,
    side: size >= 0 ? "long" : "short",
    size: Math.abs(size),
    entryPrice: parseFloat(backendPosition.entry_price),
    currentPrice: parseFloat(backendPosition.mark_price),
    leverage: parseFloat(backendPosition.leverage),
    collateral: margin,
    margin,
    liquidationPrice: backendPosition.liquidation_price ? parseFloat(backendPosition.liquidation_price) : 0,
    unrealizedPnl: parseFloat(backendPosition.unrealized_pnl),
    status: "open",
    openedAt: new Date(backendPosition.opened_at).getTime(),
  };
}

export interface OrderParams {
  market: MarketSymbol;
  side: PositionSide;
  size: number;
  price?: number; // Required for limit orders
  leverage: number;
  orderType: OrderType;
  reduceOnly?: boolean; // Flag to only reduce existing positions
  slippageTolerance?: number | null; // Maximum acceptable slippage for market orders (null = Auto/unlimited)
  timeInForce?: "GTC" | "ALO" | "IOC"; // Time-in-force policy for limit orders
  takeProfit?: number | null; // Take profit price (for limit orders)
  stopLoss?: number | null; // Stop loss price (for limit orders)
  /** Original USD value when user placed order in USD mode (preserves exact value like $5,000) */
  notionalValue?: number | null;
}

export interface OrderResult {
  success: boolean;
  position?: Position; // Frontend position format (deprecated, use backendPosition)
  backendPosition?: BackendPosition; // Backend position - store uses this directly
  order?: Order; // The order returned from backend (for limit orders to appear immediately)
  txSignature?: string; // Order ID from backend
  error?: string;
}

export interface ClosePositionResult {
  success: boolean;
  position?: Position;
  realizedPnl?: number;
  txSignature?: string;
  error?: string;
}

// Trading Engine - uses Rust backend API
export class TradingEngine {
  /**
   * Place a new order (market or limit)
   */
  static async placeOrder(
    _authority: PublicKey | string,
    params: OrderParams
  ): Promise<OrderResult> {
    try {
      // For market orders, price is determined by the backend
      // For limit orders, use the provided price
      const executionPrice = params.orderType === "limit" && params.price
        ? params.price
        : 0; // Backend will use current market price for market orders

      if (params.orderType === "limit" && !params.price) {
        return {
          success: false,
          error: "Price required for limit orders",
        };
      }

      // Build and send the order request directly
      // Account existence is ensured on wallet connect (useAccountSync)
      const request = toBackendOrderRequest(
        params.market,
        params.side,
        params.size,
        executionPrice,
        params.orderType,
        { 
          leverage: params.leverage, 
          reduceOnly: params.reduceOnly,
          slippageTolerance: params.slippageTolerance,
          timeInForce: params.timeInForce,
          takeProfit: params.takeProfit,
          stopLoss: params.stopLoss,
          notionalValue: params.notionalValue,
        }
      );

      const response = await OrdersApi.placeOrder(request);
      
      // If backend returned a position (market orders), return it directly
      // Store will use the backend position format (no unnecessary conversions)
      if (response.position) {
        return {
          success: true,
          position: undefined, // Deprecated - no longer converting to frontend format
          backendPosition: response.position, // Store uses this directly
          order: response.order, // Return order for store update
          txSignature: response.order.id,
        };
      }

      // Limit orders don't create position immediately, but still return the order
      // so it can be added to the open orders list immediately
      return {
        success: true,
        position: undefined,
        backendPosition: undefined,
        order: response.order, // Return order so it appears immediately in Open Orders
        txSignature: response.order.id,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Close an open position
   * Closes position by placing an opposite market order
   */
  static async closePosition(
    authority: PublicKey | string,
    positionId: string
  ): Promise<ClosePositionResult> {
    try {
      const userId = typeof authority === "string" ? authority : authority.toBase58();

      // Fetch positions from backend
      const backendPositions = await PositionsApi.listPositions();
      
      // Convert to frontend format and find the position
      const positions = backendPositions
        .filter((p) => parseFloat(p.size) !== 0)
        .map((p) => convertBackendToFrontendPosition(p, userId));
      
      const position = positions.find((p) => p.id === positionId);
      if (!position) {
        return {
          success: false,
          error: "Position not found",
        };
      }

      // Place an opposite market order to close the position
      const closeSide = position.side === "long" ? "short" : "long";
      const request = toBackendOrderRequest(
        position.market,
        closeSide,
        position.size,
        position.currentPrice, // Market order uses current price
        "market"
      );

      const orderResponse = await OrdersApi.placeOrder(request);

      // Use backend's realized P&L if available (most accurate)
      // Backend uses actual execution price, not stale currentPrice
      // Fallback to client-side calculation only if backend doesn't provide it
      const realizedPnl = orderResponse.realized_pnl 
        ? parseFloat(orderResponse.realized_pnl)
        : (() => {
            // Fallback calculation using current price (may be slightly inaccurate)
            const entryValue = position.size * position.entryPrice;
            const exitValue = position.size * position.currentPrice;
            return position.side === "long" 
              ? exitValue - entryValue 
              : entryValue - exitValue;
          })();

      return {
        success: true,
        position: {
          id: position.id,
          market: position.market as MarketSymbol,
          side: position.side,
          size: position.size,
          entryPrice: position.entryPrice,
          currentPrice: position.currentPrice,
          leverage: position.leverage,
          collateral: position.margin,
          margin: position.margin, // Position margin
          liquidationPrice: position.liquidationPrice,
          unrealizedPnl: realizedPnl,
          status: "closed",
          openedAt: position.openedAt, // Already a number (timestamp)
        },
        realizedPnl,
        txSignature: orderResponse.order.id,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Initialize user account if doesn't exist
   */
  static async initializeUserAccount(authority: PublicKey | string): Promise<UserAccount> {
    const userId = typeof authority === "string" ? authority : authority.toBase58();
    
    // Ensure account exists in backend
    try {
      await AccountsApi.getAccount();
    } catch {
      // Account doesn't exist -- create it (server defaults to zero collateral).
      // Balances are server-authoritative; the client never sends collateral.
      await AccountsApi.createAccount();
    }
    
    // Return dummy UserAccount for backwards compatibility
    return {
      authority: userId,
      collateral: 0,
      availableBalance: 0,
      usedMargin: 0,
      totalPnl: 0,
      positions: [],
    };
  }
}

